import { supabase } from './supabase';

export const webhooksService = {
  async getWebhooks(activeOnly = false) {
    try {
      let query = supabase.from('webhooks').select('*');
      
      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, webhooks: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async getWebhookById(webhookId) {
    try {
      const { data, error } = await supabase
        .from('webhooks')
        .select('*')
        .eq('id', webhookId)
        .single();

      if (error) throw error;
      return { success: true, webhook: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async createWebhook(name, url, type, eventTypes, createdBy, channel = '', isMediaWebhook = false) {
    try {
      const { data, error } = await supabase
        .from('webhooks')
        .insert([{
          name,
          url,
          type,
          event_types: eventTypes.join(','),
          created_by: createdBy,
          channel: channel || null,
          is_media_webhook: isMediaWebhook
        }])
        .select()
        .single();

      if (error) throw error;
      return { success: true, webhook: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async updateWebhook(webhookId, updates) {
    try {
      const processedUpdates = { ...updates };
      if (processedUpdates.eventTypes) {
        processedUpdates.event_types = processedUpdates.eventTypes.join(',');
        delete processedUpdates.eventTypes;
      }
      if (processedUpdates.isMediaWebhook !== undefined) {
        processedUpdates.is_media_webhook = processedUpdates.isMediaWebhook;
        delete processedUpdates.isMediaWebhook;
      }

      const { data, error } = await supabase
        .from('webhooks')
        .update({ ...processedUpdates, updated_at: new Date().toISOString() })
        .eq('id', webhookId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, webhook: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async deleteWebhook(webhookId) {
    try {
      const { error } = await supabase
        .from('webhooks')
        .delete()
        .eq('id', webhookId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async testWebhook(webhookId) {
    try {
      const webhook = await this.getWebhookById(webhookId);
      if (!webhook.success) throw new Error('Webhook not found');

      const testPayload = {
        message: 'Test webhook notification'
      };

      const body = webhook.webhook.type === 'generic'
        ? JSON.stringify({
            event: 'test',
            timestamp: new Date().toISOString(),
            message: 'Test webhook notification'
          })
        : JSON.stringify(this.buildPayload(webhook.webhook.type, 'test', testPayload));

      const response = await fetch(webhook.webhook.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body
      });

      const responseText = await response.text();
      
      await this.logWebhookCall(
        webhookId,
        'test',
        testPayload,
        response.status,
        responseText,
        response.ok
      );

      return {
        success: response.ok,
        status: response.status,
        response: responseText
      };
    } catch (error) {
      const errorMessage = error?.message || 'Unknown error occurred';
      return { success: false, error: errorMessage };
    }
  },

  async triggerWebhooks(eventType, payload) {
    try {
      const webhooksResult = await this.getWebhooks(true);
      if (!webhooksResult.success) return;

      const activeWebhooks = webhooksResult.webhooks.filter(w => 
        w.event_types.split(',').includes(eventType)
      );

      for (const webhook of activeWebhooks) {
        try {
          await this.sendWebhook(webhook, eventType, payload);
        } catch (error) {
          console.error(`Failed to send webhook ${webhook.id}:`, error);
        }
      }
    } catch (error) {
      console.error('Error triggering webhooks:', error);
    }
  },

  async triggerSpecificWebhooks(webhookIds, eventType, payload) {
    try {
      const webhooksResult = await this.getWebhooks(true);
      if (!webhooksResult.success) return;

      const targetWebhooks = webhooksResult.webhooks.filter(w => 
        webhookIds.includes(w.id)
      );

      for (const webhook of targetWebhooks) {
        try {
          await this.sendWebhook(webhook, eventType, payload);
        } catch (error) {
          console.error(`Failed to send webhook ${webhook.id}:`, error);
        }
      }
    } catch (error) {
      console.error('Error triggering specific webhooks:', error);
    }
  },

  async sendWebhook(webhook, eventType, payload) {
    try {
      const body = webhook.type === 'generic' 
        ? JSON.stringify({
            event: eventType,
            timestamp: new Date().toISOString(),
            data: payload
          })
        : JSON.stringify(this.buildPayload(webhook.type, eventType, payload));

      const response = await fetch(webhook.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body
      });

      const responseText = await response.text();

      await this.logWebhookCall(
        webhook.id,
        eventType,
        payload,
        response.status,
        responseText,
        response.ok
      );

      if (response.ok) {
        await supabase
          .from('webhooks')
          .update({ last_triggered_at: new Date().toISOString() })
          .eq('id', webhook.id);
      }

      return { success: response.ok };
    } catch (error) {
      await this.logWebhookCall(
        webhook.id,
        eventType,
        payload,
        0,
        error.message,
        false
      );
      throw error;
    }
  },

  async logWebhookCall(webhookId, eventType, payload, statusCode, response, success, messageId = null, externalId = null) {
    try {
      const logData = {
        webhook_id: webhookId,
        event_type: eventType,
        payload: payload,
        status_code: statusCode,
        response: response,
        success: success
      };

      if (messageId) logData.message_id = messageId;
      if (externalId) logData.external_id = externalId;

      await supabase
        .from('webhook_logs')
        .insert([logData]);
    } catch (error) {
      console.error('Failed to log webhook call:', error);
    }
  },

  async getWebhookLogs(webhookId, limit = 50) {
    try {
      const { data, error } = await supabase
        .from('webhook_logs')
        .select('*')
        .eq('webhook_id', webhookId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return { success: true, logs: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async getLastMessageForEvent(webhookId, eventType) {
    try {
      const { data, error } = await supabase
        .from('webhook_logs')
        .select('*')
        .eq('webhook_id', webhookId)
        .eq('event_type', eventType)
        .not('message_id', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return { success: true, log: data || null };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async sendWebhookMessage(webhookId, eventType, data, editMessageId = null) {
    try {
      const webhook = await this.getWebhookById(webhookId);
      if (!webhook.success) throw new Error('Webhook not found');

      const payload = this.buildPayload(webhook.webhook.type, eventType, data);
      
      let url = webhook.webhook.url;
      let method = 'POST';

      if (editMessageId) {
        if (webhook.webhook.type === 'discord') {
          url = `${webhook.webhook.url}/messages/${editMessageId}`;
          method = 'PATCH';
        } else {
          throw new Error('Message editing only supported for Discord webhooks');
        }
      }

      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      let messageId = null;

      if (webhook.webhook.type === 'discord' && response.ok) {
        try {
          const responseJson = JSON.parse(responseText);
          messageId = responseJson.id;
        } catch (e) {
          console.warn('Could not parse Discord message ID from response');
        }
      }

      await this.logWebhookCall(
        webhookId,
        eventType,
        data,
        response.status,
        responseText,
        response.ok,
        messageId
      );

      return {
        success: response.ok,
        status: response.status,
        response: responseText,
        messageId: messageId
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  buildPayload(webhookType, eventType, data) {
    if (webhookType === 'discord') {
      return this.buildDiscordPayload(eventType, data);
    } else if (webhookType === 'slack') {
      return this.buildSlackPayload(eventType, data);
    } else {
      return {
        event: eventType,
        timestamp: new Date().toISOString(),
        data: data
      };
    }
  },

  buildDiscordPayload(eventType, data) {
    let color = 0x1e88e5;
    let title = data.title || (eventType === 'test' ? 'Test Notification' : eventType);
    let description = data.description || data.message || '';

    if (eventType === 'rankings_updated') {
      color = 0xffa500;
    } else if (eventType === 'schedule_updated') {
      color = 0x9c27b0;
    } else if (eventType === 'scores_updated') {
      color = 0x4caf50;
    } else if (eventType === 'news_published') {
      color = 0x2196f3;
    } else if (eventType === 'test') {
      color = 0x9c9c9c;
    }

    const embed = {
      title: title,
      description: description,
      color: color,
      timestamp: new Date().toISOString()
    };

    if (data.url) embed.url = data.url;
    if (data.author) embed.author = { name: data.author };
    if (data.image) embed.image = { url: data.image };
    if (data.header_image) embed.image = { url: data.header_image };
    if (data.fields && Array.isArray(data.fields)) {
      embed.fields = data.fields;
    }

    return { embeds: [embed] };
  },

  buildSlackPayload(eventType, data) {
    let color = '#1e88e5';
    if (eventType === 'rankings_updated') color = '#ffa500';
    else if (eventType === 'schedule_updated') color = '#9c27b0';
    else if (eventType === 'scores_updated') color = '#4caf50';

    return {
      attachments: [{
        color: color,
        title: data.title || eventType,
        text: data.description || '',
        fields: data.fields || [],
        ts: Math.floor(Date.now() / 1000)
      }]
    };
  }
};
